import { useState, useEffect, useRef } from "react";
import { useNavigate, Navigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Confetti } from "@/components/Confetti";

import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useAdmin } from "@/hooks/useAdmin";
import { supabase } from "@/integrations/supabase/client";
import { useServices, ServiceCategoryDB as ServiceCategory, SubServiceDB as SubService } from "@/hooks/useServices";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowDown, Check, CalendarDays, Clock, X, DollarSign, PartyPopper, Search, UserPlus, User, CreditCard, Loader2, Camera, AlertTriangle, Tag, Gift, Plus, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useServiceAddons, ServiceAddon } from "@/hooks/useServiceAddons";
import RebookBar, { PastBooking, resolveRebookService } from "@/components/RebookBar";
import ClientAvatar from "@/components/ClientAvatar";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingsOpenBanner from "@/components/BookingsOpenBanner";
import AddToCalendarButtons from "@/components/AddToCalendarButtons";
import { useClientPhotoCheck } from "@/components/ClientPhotoUpload";
import { isTestAccount } from "@/lib/testAccounts";
import { logSmsConsent, SMS_CONSENT_TEXT } from "@/lib/smsConsent";
import { logBookingFailure } from "@/lib/bookingFailures";
import SEO from "@/components/SEO";

const BOOK_OG: Record<string, { title: string; description: string; image: string }> = {
  nails: {
    title: "Book Your Nails Appointment — In Paradise Beauty",
    description: "Reserve your luxury nail experience at In Paradise Beauty.",
    image: "/og-book-nails.jpg",
  },
  lashes: {
    title: "Book Your Lash Appointment — In Paradise Beauty",
    description: "Reserve your lash artistry session at In Paradise Beauty.",
    image: "/og-book-lashes.jpg",
  },
  pedicures: {
    title: "Book Your Pedicure Appointment — In Paradise Beauty",
    description: "Treat your feet to a luxury pedicure at In Paradise Beauty.",
    image: "/og-book-pedicures.jpg",
  },
  dermaplaning: {
    title: "Book Your Dermaplaning Appointment — In Paradise Beauty",
    description: "Reveal your glow with dermaplaning at In Paradise Beauty.",
    image: "/og-book-dermaplaning.jpg",
  },
};

const ALL_TIME_SLOTS = [
  "9:00 AM", "9:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM",
  "3:00 PM", "3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM", "5:30 PM", "6:00 PM",
];

// Convert "9:00 AM" -> minutes from midnight (e.g. 540)
const timeToMinutes = (t: string): number => {
  const [time, period] = t.split(" ");
  let [h, m] = time.split(":").map(Number);
  if (period === "PM" && h !== 12) h += 12;
  if (period === "AM" && h === 12) h = 0;
  return h * 60 + m;
};

// Convert "HH:MM:SS" or "HH:MM" (24h) -> minutes from midnight
const dbTimeToMinutes = (t: string): number => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

// Format a Date as a local YYYY-MM-DD string (avoids UTC off-by-one from toISOString)
const toLocalDateStr = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const steps = ["Select Experience", "Pick Date & Time", "Confirm"];

const beforeItems = [
  "Arrive on Time",
  "Confirm Booking Time",
  "Come with Bare Nails",
  "Ensure Deposit is Paid",
  "Use the Restroom Before",
  "Dress Comfortably",
  "No Extra Guests",
  "Cancel if Feeling Sick",
];

const getPolicies = (depositLabel: string) => [
  {
    icon: <X size={28} className="text-muted-foreground" />,
    title: "Cancellation & Rescheduling",
    text: "Please cancel or reschedule 24 hours before your appointment. A new deposit is required if you reschedule within 24 hrs. No-call no-shows will be charged half of their service.",
  },
  {
    icon: <DollarSign size={28} className="text-muted-foreground" />,
    title: "Deposits",
    text: depositLabel
      ? `All services require a non-refundable ${depositLabel} deposit to secure your appointment.`
      : "A deposit may be required to secure your appointment.",
  },
  {
    icon: <Clock size={28} className="text-muted-foreground" />,
    title: "Late Policy",
    text: "Please arrive on time. After the 10 minute grace period, there will be a $10 late fee; after 20 minutes, your appointment will be cancelled.",
  },
  {
    icon: <Sparkles size={28} className="text-muted-foreground" />,
    title: "Bare Nails / Feet / Lashes",
    text: "Please come with bare nails or lashes. There will be an additional 15% fee on your total for any work that has to be removed.",
  },
];

const PoliciesSection = ({ interactive, confirmedPolicies, onConfirmPolicy, policies, hintPulse, highlightUnconfirmed }: {
  interactive?: boolean;
  confirmedPolicies?: Record<number, boolean>;
  onConfirmPolicy?: (index: number) => void;
  policies: ReturnType<typeof getPolicies>;
  hintPulse?: boolean;
  highlightUnconfirmed?: boolean;
}) => {
  const policyRefs = useRef<(HTMLDivElement | null)[]>([]);

  const handleConfirm = (index: number) => {
    if (!interactive || !onConfirmPolicy) return;
    if (confirmedPolicies?.[index]) return;
    onConfirmPolicy(index);
    // Auto-scroll to next unconfirmed policy
    const nextIndex = policies.findIndex((_, i) => i > index && !confirmedPolicies?.[i]);
    if (nextIndex !== -1 && policyRefs.current[nextIndex]) {
      setTimeout(() => {
        policyRefs.current[nextIndex]?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="font-serif text-2xl md:text-3xl font-bold italic text-center mb-6 tracking-wide">
          Before Your Scheduled Time
        </h3>
        <div className="grid grid-cols-2 gap-3">
          {beforeItems.map((item) => (
            <div
              key={item}
              className="bg-muted/50 border border-border/50 py-3 px-4 text-center text-xs tracking-[0.2em] uppercase font-light text-muted-foreground"
            >
              {item}
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-serif text-2xl md:text-3xl font-bold italic text-center mb-6 tracking-wide">
          Booking Policies
          {interactive && (
            <motion.span
              className={`block text-xs font-normal not-italic mt-2 tracking-normal ${hintPulse ? "text-primary font-semibold" : "text-muted-foreground"}`}
              animate={hintPulse ? { scale: [1, 1.15, 1], opacity: [1, 0.7, 1] } : {}}
              transition={hintPulse ? { duration: 0.6, repeat: 2 } : {}}
            >
              Tap each policy to confirm you've read it
            </motion.span>
          )}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {policies.map((policy, index) => {
            const isConfirmed = confirmedPolicies?.[index];
            const needsAttention = highlightUnconfirmed && !isConfirmed;
            return (
              <div
                key={policy.title}
                ref={(el) => { policyRefs.current[index] = el; }}
                onClick={() => handleConfirm(index)}
                className={`relative border p-6 text-center transition-all duration-300 ${
                  interactive
                    ? isConfirmed
                      ? "border-primary/50 bg-primary/5 ring-1 ring-primary/30"
                      : needsAttention
                        ? "border-destructive ring-2 ring-destructive/50 cursor-pointer hover:border-primary/40 hover:shadow-md group"
                        : "border-border/50 cursor-pointer hover:border-primary/40 hover:shadow-md group"
                    : "border-border/50"
                }`}
              >
                {/* Arrow indicator for unconfirmed policies */}
                {needsAttention && (
                  <motion.div
                    className="absolute -top-8 left-1/2 -translate-x-1/2 flex flex-col items-center"
                    animate={{ y: [0, 6, 0] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                  >
                    <span className="text-[10px] tracking-wider uppercase font-semibold text-destructive whitespace-nowrap">Tap here</span>
                    <ArrowDown size={16} className="text-destructive" />
                  </motion.div>
                )}
                {/* Hover tooltip */}
                {interactive && !isConfirmed && (
                  <div className="absolute inset-0 flex items-center justify-center bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none">
                    <span className="text-xs tracking-[0.2em] uppercase font-medium text-primary">
                      Press to confirm acceptance
                    </span>
                  </div>
                )}
                {/* Confirmed checkmark */}
                {interactive && isConfirmed && (
                  <div className="absolute top-2 right-2">
                    <Check size={18} className="text-primary" />
                  </div>
                )}
                <div className="flex justify-center mb-3">{policy.icon}</div>
                <h4 className="text-xs tracking-[0.3em] uppercase font-medium mb-3">{policy.title}</h4>
                <p className="text-muted-foreground text-sm font-extralight leading-relaxed">{policy.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface ClientProfile {
  id: string;
  user_id: string;
  full_name: string | null;
  phone: string | null;
  email: string | null;
}

const ClientSearch = ({
  selectedClient,
  onSelectClient,
}: {
  selectedClient: ClientProfile | null;
  onSelectClient: (client: ClientProfile | null) => void;
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ClientProfile[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setSearching(true);
      const { data } = await supabase
        .from("profiles")
        .select("id, user_id, full_name, phone, email")
        .or(`full_name.ilike.%${query}%,phone.ilike.%${query}%,email.ilike.%${query}%`)
        .limit(10);
      setResults(data || []);
      setSearching(false);
      setOpen(true);
    }, 300);
    return () => clearTimeout(timeout);
  }, [query]);

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handleCreateClient = async () => {
    if (!newName.trim()) {
      toast.error("Client name is required");
      return;
    }
    if (!newPhone || newPhone.replace(/\D/g, "").length < 10) {
      toast.error("Valid phone number is required");
      return;
    }
    const emailTrim = (newEmail || "").trim().toLowerCase();
    if (!emailTrim || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      toast.error("A valid email address is required");
      return;
    }
    setCreating(true);
    try {
      const digits = newPhone.replace(/\D/g, "");
      const e164 = digits.length === 10 ? `+1${digits}` : `+${digits}`;

      // Create auth user via admin-auth edge function
      const { data: authData, error: authError } = await supabase.functions.invoke("admin-auth", {
        body: {
          action: "create_client",
          email: emailTrim,
          full_name: newName.trim(),
          phone: e164,
        },
      });
      if (authError) throw authError;

      const userId = authData?.user_id;
      if (authData?.already_exists && userId) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("id, user_id, full_name, phone, email")
          .eq("user_id", userId)
          .maybeSingle();
        if (profile) {
          onSelectClient(profile);
          toast.info(authData.error || "Existing client selected.");
        } else {
          toast.error(authData.error || "Client already exists.");
        }
      } else {
        if (authData?.error) throw new Error(authData.error);
        if (userId) {
          const { data: profile } = await supabase
            .from("profiles")
            .select("id, user_id, full_name, phone, email")
            .eq("user_id", userId)
            .maybeSingle();
          if (profile) {
            onSelectClient(profile);
            toast.success("Client created successfully!");
          }
        }
      }
      setShowNewForm(false);
      setNewName("");
      setNewPhone("");
      setNewEmail("");
    } catch (err: any) {
      toast.error(err.message || "Failed to create client");
    }
    setCreating(false);
  };

  if (selectedClient) {
    return (
      <div className="bg-card border border-primary/30 p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClientAvatar userId={selectedClient.user_id} name={selectedClient.full_name} size="md" />
          <div>
            <p className="font-semibold text-sm">{selectedClient.full_name || "Unnamed"}</p>
            <p className="text-xs text-muted-foreground">{selectedClient.phone} {selectedClient.email && `• ${selectedClient.email}`}</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => onSelectClient(null)} className="text-xs">
          Change
        </Button>
      </div>
    );
  }

  if (showNewForm) {
    return (
      <div className="bg-card border border-border/50 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium tracking-wide flex items-center gap-2">
            <UserPlus size={16} className="text-primary" /> New Client
          </h4>
          <Button variant="ghost" size="sm" onClick={() => setShowNewForm(false)} className="text-xs text-muted-foreground">
            Cancel
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs tracking-wide uppercase text-muted-foreground">Full Name *</label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Client name"
              className="bg-background border-border/50 mt-1"
            />
          </div>
          <div>
            <label className="text-xs tracking-wide uppercase text-muted-foreground">Phone *</label>
            <Input
              type="tel"
              value={newPhone}
              onChange={(e) => setNewPhone(formatPhone(e.target.value))}
              placeholder="(555) 123-4567"
              maxLength={14}
              className="bg-background border-border/50 mt-1"
            />
          </div>
        </div>
        <div>
          <label className="text-xs tracking-wide uppercase text-muted-foreground">Email <span className="text-destructive">*</span></label>
          <Input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="client@example.com"
            required
            className="bg-background border-border/50 mt-1"
          />
        </div>
        <Button onClick={handleCreateClient} disabled={creating} className="text-xs tracking-widest uppercase w-full">
          {creating ? "Creating…" : "Create Client"}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row gap-3 sm:items-start">
      {/* Dropdown search */}
      <div className="flex-1 relative">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => { if (results.length > 0) setOpen(true); }}
            onBlur={() => setTimeout(() => setOpen(false), 200)}
            placeholder="Search clients by name, phone, or email..."
            className="pl-10 bg-background border-border/50"
          />
        </div>
        {open && (
          <div className="absolute z-50 left-0 right-0 mt-1 border border-border/50 bg-card rounded-md shadow-lg max-h-56 overflow-y-auto">
            {searching && (
              <div className="px-4 py-3 text-xs text-muted-foreground">Searching…</div>
            )}
            {!searching && results.length > 0 && results.map((client) => (
              <button
                key={client.id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onSelectClient(client); setQuery(""); setResults([]); setOpen(false); }}
                className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border/30 last:border-b-0"
              >
                <p className="text-sm font-medium">{client.full_name || "Unnamed"}</p>
                <p className="text-xs text-muted-foreground">{client.phone} {client.email && `• ${client.email}`}</p>
              </button>
            ))}
            {!searching && query.length >= 2 && results.length === 0 && (
              <div className="px-4 py-3 text-xs text-muted-foreground">No clients found for "{query}"</div>
            )}
          </div>
        )}
      </div>

      {/* Add new client button */}
      <Button
        variant="outline"
        onClick={() => setShowNewForm(true)}
        className="shrink-0 rounded-none border-primary/30 hover:bg-primary/10 tracking-[0.15em] uppercase text-xs font-medium gap-2"
      >
        <UserPlus size={14} /> New Client
      </Button>
    </div>
  );
};

const Book = () => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin } = useAdmin();
  const { categories: services, loading: servicesLoading } = useServices();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStepRaw] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | null>(null);
  const [selectedService, setSelectedService] = useState<SubService | null>(null);
  const [selectedAddons, setSelectedAddons] = useState<ServiceAddon[]>([]);
  const { addons: availableAddons } = useServiceAddons(selectedCategory?.id);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const subServicesRef = useRef<HTMLDivElement>(null);
  const addonsRef = useRef<HTMLDivElement>(null);
  const policiesRef = useRef<HTMLDivElement>(null);

  const setStep = (s: number) => {
    setStepRaw(s);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const [submitting, setSubmitting] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [policiesAcknowledged, setPoliciesAcknowledged] = useState(false);
  const [confirmAcknowledged, setConfirmAcknowledged] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingBlocked, setBookingBlocked] = useState(false);
  const [blockLoading, setBlockLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState<ClientProfile | null>(null);
  const [paymentType, setPaymentType] = useState<"deposit" | "full">("deposit");
  const [depositAmount, setDepositAmount] = useState(0);
  const [depositType, setDepositType] = useState<"fixed" | "percentage">("fixed");
  const [depositEnabled, setDepositEnabled] = useState(true);
  const [photoSettings, setPhotoSettings] = useState({
    requireFirst: false,
    requireSubsequent: false,
    requirePedicure: false,
    enforcement: "warn" as "block" | "warn",
  });
  const [isFirstBooking, setIsFirstBooking] = useState(true);
  const [bookingCount, setBookingCount] = useState(0);
  const [expressPolicyOpen, setExpressPolicyOpen] = useState(false);
  const [expressPolicyPrompted, setExpressPolicyPrompted] = useState(false);

  const [photoWarningDismissed, setPhotoWarningDismissed] = useState(false);
  const [lashInspoOpen, setLashInspoOpen] = useState(false);
  const [lashInspoPromptedFor, setLashInspoPromptedFor] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupons, setAppliedCoupons] = useState<{ code: string; discount_amount: number; id: string; is_promotional?: boolean; discount_type?: string }[]>([]);
  const [couponLoading, setCouponLoading] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState<{ id: string; code: string; discount_amount: number; expires_at: string }[]>([]);
  const [bookingHours, setBookingHours] = useState<{ day_of_week: number; start_time: string; end_time: string; is_available: boolean }[]>([]);
  const [blockedDates, setBlockedDates] = useState<{ blocked_date: string; all_day: boolean; start_time: string | null; end_time: string | null }[]>([]);
  const [bookedSlots, setBookedSlots] = useState<{ start_min: number; end_min: number }[]>([]);

  const [confirmedPolicies, setConfirmedPolicies] = useState<Record<number, boolean>>({});
  const [policyAttemptCount, setPolicyAttemptCount] = useState(0);
  const [hintPulse, setHintPulse] = useState(false);
  const [referralCodeInput, setReferralCodeInput] = useState("");
  const [referralApplied, setReferralApplied] = useState(false);
  const [referralLoading, setReferralLoading] = useState(false);

  // Inspiration photos uploaded by the client for this booking
  const [inspirationPhotos, setInspirationPhotos] = useState<{ id: string; file_path: string; url: string }[]>([]);
  const [inspirationUploading, setInspirationUploading] = useState(false);
  const inspirationInputRef = useRef<HTMLInputElement>(null);

  // Admin skips policies
  const adminMode = isAdmin;

  // Returning clients (3+ past bookings) get a one-tap policy acknowledgement
  const isReturningClient = !adminMode && !!user && bookingCount >= 3;


  // Auto-select service and addons from URL params (when navigating from services page)
  useEffect(() => {
    if (servicesLoading || services.length === 0) return;
    const categoryParam = searchParams.get("category");
    const serviceParam = searchParams.get("service");
    const addonsParam = searchParams.get("addons");
    if (categoryParam && serviceParam && !selectedService) {
      const cat = services.find((c) => c.title === categoryParam);
      if (cat) {
        setSelectedCategory(cat);
        const sub = cat.subServices.find((s) => s.name === serviceParam);
        if (sub) {
          setSelectedService(sub);

          // Pre-select addons from URL if provided
          if (addonsParam) {
            const addonNames = addonsParam.split(",").map((n) => n.trim());
            // We need to wait for availableAddons to load, so store names for later matching
            sessionStorage.setItem("preselect_addons", JSON.stringify(addonNames));
          }

          setTimeout(() => {
            const target = addonsRef.current || policiesRef.current;
            target?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 200);
        }
      }
    }
  }, [servicesLoading, services, searchParams]);

  // Pre-fill date from ?date=YYYY-MM-DD (e.g. from Bookings date filter)
  useEffect(() => {
    const dateParam = searchParams.get("date");
    if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam) && !selectedDate) {
      const [y, m, d] = dateParam.split("-").map(Number);
      setSelectedDate(new Date(y, m - 1, d));
    }
  }, [searchParams]);

  // Pre-select addons once they're loaded
  useEffect(() => {
    const stored = sessionStorage.getItem("preselect_addons");
    if (stored && availableAddons.length > 0) {
      try {
        const addonNames: string[] = JSON.parse(stored);
        const matched = availableAddons.filter((a) => addonNames.includes(a.name));
        if (matched.length > 0) {
          setSelectedAddons(matched);
        }
      } catch {}
      sessionStorage.removeItem("preselect_addons");
    }
  }, [availableAddons]);

  // Auto-select client from URL params (when navigating from client detail page)
  useEffect(() => {
    if (!adminMode) return;
    const clientId = searchParams.get("clientId");
    const clientName = searchParams.get("clientName");
    if (clientId && !selectedClient) {
      const fetchClient = async () => {
        const { data } = await supabase
          .from("profiles")
          .select("id, user_id, full_name, phone, email")
          .eq("user_id", clientId)
          .maybeSingle();
        if (data) {
          setSelectedClient(data as ClientProfile);
        } else if (clientName) {
          // Fallback: set minimal client info
          setSelectedClient({ id: "", user_id: clientId, full_name: clientName, phone: null, email: null });
        }
      };
      fetchClient();
    }
  }, [adminMode, searchParams]);

  const checkUserId = adminMode && selectedClient ? selectedClient.user_id : user?.id;
  const { hasPhoto, loading: photosLoading } = useClientPhotoCheck(checkUserId);

  // Compute actual deposit for display
  const computedDeposit = depositType === "percentage" && selectedService
    ? Math.round(selectedService.price * depositAmount / 100)
    : depositAmount;

  // Test accounts bypass the deposit requirement entirely
  const bookingForUserId = adminMode && selectedClient ? selectedClient.user_id : user?.id;
  const isTestBookingUser = isTestAccount(bookingForUserId);

  // Reschedule flow: client is moving an existing confirmed booking. The
  // original deposit carries over, no new deposit/payment is required, and
  // the new booking is created as `pending` for admin to confirm.
  const isReschedule = searchParams.get("reschedule") === "true";
  const originalBookingId = searchParams.get("originalBookingId");

  const effectiveDepositEnabled = depositEnabled && !isTestBookingUser && !isReschedule;

  const depositLabel = effectiveDepositEnabled && computedDeposit > 0 ? `$${computedDeposit}` : "";
  const policies = getPolicies(depositLabel);

  const acceptAllPolicies = () => {
    const all: Record<number, boolean> = {};
    policies.forEach((_, i) => { all[i] = true; });
    setConfirmedPolicies(all);
    setConfirmAcknowledged(true);
    setPolicyAttemptCount(0);
    setHintPulse(false);
    setExpressPolicyOpen(false);
  };

  // One-time "account upgraded" announcement for returning clients
  useEffect(() => {
    if (step === 2 && isReturningClient && user && !expressPolicyPrompted && !confirmAcknowledged) {
      setExpressPolicyPrompted(true);
      const key = `express_policy_upgrade_seen_${user.id}`;
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
      setExpressPolicyOpen(true);
    }
  }, [step, isReturningClient, user, expressPolicyPrompted, confirmAcknowledged]);



  // Auto-select full payment when deposit is $0 (or skipped for reschedules)
  useEffect(() => {
    if ((computedDeposit <= 0 || isReschedule) && paymentType === "deposit") {
      setPaymentType("full");
    }
  }, [computedDeposit, paymentType, isReschedule]);

  // Fetch deposit + photo settings
  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await supabase.from("site_settings").select("key, value").in("key", [
        "deposit_enabled", "deposit_amount", "deposit_type",
        "require_photos_first", "require_photos_subsequent",
        "require_pedicure_photo", "photo_enforcement_mode",
      ]);
      if (data) {
        const get = (k: string) => data.find((r: any) => r.key === k)?.value;
        const enabled = get("deposit_enabled") !== "false";
        setDepositEnabled(enabled);
        if (enabled) {
          if (get("deposit_amount") !== undefined) setDepositAmount(Number(get("deposit_amount")) || 0);
          if (get("deposit_type")) setDepositType(get("deposit_type") === "percentage" ? "percentage" : "fixed");
        } else {
          setDepositAmount(0);
        }
        setPhotoSettings({
          requireFirst: get("require_photos_first") === "true",
          requireSubsequent: get("require_photos_subsequent") === "true",
          requirePedicure: get("require_pedicure_photo") === "true",
          enforcement: get("photo_enforcement_mode") === "block" ? "block" : "warn",
        });
      }
    };
    fetchSettings();
  }, []);

  // Fetch booking hours and blocked dates for availability enforcement
  useEffect(() => {
    const fetchAvailability = async () => {
      const [hoursRes, blockedRes] = await Promise.all([
        supabase.from("booking_hours").select("day_of_week, start_time, end_time, is_available"),
        supabase.from("blocked_dates").select("blocked_date, all_day, start_time, end_time"),
      ]);
      if (hoursRes.data) setBookingHours(hoursRes.data);
      if (blockedRes.data) setBlockedDates(blockedRes.data);
    };
    fetchAvailability();
  }, []);

  // Fetch booked time windows for the selected date so we can hide slots
  // that fall within 15 minutes of an existing appointment.
  useEffect(() => {
    if (!selectedDate) { setBookedSlots([]); return; }
    const dateStr = toLocalDateStr(selectedDate);
    let cancelled = false;
    const load = async () => {
      const { data, error } = await supabase.rpc("get_booked_slots", { p_date: dateStr });
      if (cancelled) return;
      if (error || !data) { setBookedSlots([]); return; }
      // Exclude the booking currently being rescheduled (if any) so it doesn't block itself
      const filtered = (data as { start_min: number; end_min: number }[]).filter(Boolean);
      setBookedSlots(filtered);
    };
    load();
    return () => { cancelled = true; };
  }, [selectedDate]);

  // Compute whether a date is disabled (closed day or fully blocked)
  const isDateDisabled = (date: Date): boolean => {
    if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
    // Enforce 48-hour minimum lead time (clients only). If the entire day is
    // within 48h of now, disable it. A date is bookable only if its end-of-day
    // is more than 48h away (i.e. at least one slot on it could clear the cutoff).
    if (!adminMode) {
      const cutoff = new Date(Date.now() + 48 * 60 * 60 * 1000);
      const endOfDate = new Date(date);
      endOfDate.setHours(23, 59, 59, 999);
      if (endOfDate < cutoff) return true;
    }
    const dow = date.getDay();
    // Check booking hours - if we have data and this day is marked unavailable
    if (bookingHours.length > 0) {
      const dayHours = bookingHours.find((h) => h.day_of_week === dow);
      if (!dayHours || !dayHours.is_available) return true;
    }
    // Check if entire date is blocked (all_day)
    const dateStr = toLocalDateStr(date);
    const allDayBlocked = blockedDates.some((b) => b.blocked_date === dateStr && b.all_day);
    if (allDayBlocked) return true;
    return false;
  };

  // Compute available time slots for the selected date
  const slotComputation = (() => {
    let pastCloseSlots: string[] = [];
    let apptDurationMin = 0;
    let closingMin: number | null = null;

    if (!selectedDate) {
      return { slots: ALL_TIME_SLOTS, pastCloseSlots, apptDurationMin, closingMin };
    }
    const dow = selectedDate.getDay();
    const dateStr = toLocalDateStr(selectedDate);

    // Get operating hours for this day
    const dayHours = bookingHours.find((h) => h.day_of_week === dow);
    let slots = ALL_TIME_SLOTS;

    // Filter by booking hours window
    if (dayHours && dayHours.is_available) {
      const openMin = dbTimeToMinutes(dayHours.start_time);
      const closeMin = dbTimeToMinutes(dayHours.end_time);
      slots = slots.filter((t) => {
        const min = timeToMinutes(t);
        return min >= openMin && min < closeMin;
      });
    }

    // Compute appointment length up-front so partial-block filtering can use it
    const parseDur = (s: string): number => {
      if (!s) return 0;
      const lower = s.toLowerCase();
      let total = 0;
      const hr = lower.match(/(\d+(?:\.\d+)?)\s*(?:h|hr|hour)/);
      if (hr) total += parseFloat(hr[1]) * 60;
      const mn = lower.match(/(\d+)\s*(?:m|min)/);
      if (mn) total += parseInt(mn[1], 10);
      if (total === 0) {
        const bare = lower.match(/^\s*(\d+)\s*$/);
        if (bare) total = parseInt(bare[1], 10);
      }
      return total;
    };
    const apptMin = selectedService
      ? parseDur(selectedService.duration) +
        selectedAddons.reduce((sum, a) => sum + parseDur(a.duration), 0)
      : 0;

    // Remove time slots that overlap any partial block window.
    // A slot overlaps if start < blockEnd AND end > blockStart.
    const dateBlocks = blockedDates.filter((b) => b.blocked_date === dateStr && !b.all_day && b.start_time && b.end_time);
    if (dateBlocks.length > 0) {
      slots = slots.filter((t) => {
        const start = timeToMinutes(t);
        const end = start + Math.max(apptMin, 1);
        return !dateBlocks.some((b) => {
          const blockStart = dbTimeToMinutes(b.start_time!);
          const blockEnd = dbTimeToMinutes(b.end_time!);
          return start < blockEnd && end > blockStart;
        });
      });
    }

    // Enforce a 15-minute buffer between appointments. Treat each existing
    // booking as occupying [start - 15, end + 15] so a new appointment cannot
    // be scheduled within 15 minutes on either side.
    const BUFFER_MIN = 15;
    if (bookedSlots.length > 0) {
      slots = slots.filter((t) => {
        const start = timeToMinutes(t);
        const end = start + Math.max(apptMin, 1);
        return !bookedSlots.some((b) => {
          const padStart = b.start_min - BUFFER_MIN;
          const padEnd = b.end_min + BUFFER_MIN;
          return start < padEnd && end > padStart;
        });
      });
    }
    // Track slots removed specifically because the appointment would extend past close
    // so we can surface a helpful message in the UI.
    if (dayHours && dayHours.is_available && selectedService && apptMin > 0) {
      const closeMin = dbTimeToMinutes(dayHours.end_time);
      const kept: string[] = [];
      const past: string[] = [];
      for (const t of slots) {
        if (timeToMinutes(t) + apptMin <= closeMin) kept.push(t);
        else past.push(t);
      }
      slots = kept;
      pastCloseSlots = past;
      apptDurationMin = apptMin;
      closingMin = closeMin;
    }

    // Filter out past time slots if selected date is today
    const now = new Date();
    const todayStr = toLocalDateStr(now);
    if (dateStr === todayStr) {
      const currentMinutes = now.getHours() * 60 + now.getMinutes();
      slots = slots.filter((t) => timeToMinutes(t) > currentMinutes);
      pastCloseSlots = pastCloseSlots.filter((t) => timeToMinutes(t) > currentMinutes);
    }

    // Enforce 48-hour minimum lead time (clients only)
    if (!adminMode) {
      const cutoff = new Date(Date.now() + 48 * 60 * 60 * 1000);
      const leadFilter = (t: string) => {
        const slotDate = new Date(selectedDate);
        slotDate.setHours(0, 0, 0, 0);
        const min = timeToMinutes(t);
        slotDate.setMinutes(slotDate.getMinutes() + min);
        return slotDate >= cutoff;
      };
      slots = slots.filter(leadFilter);
      pastCloseSlots = pastCloseSlots.filter(leadFilter);
    }

    return { slots, pastCloseSlots, apptDurationMin, closingMin };
  })();
  const availableTimeSlots = slotComputation.slots;
  const pastCloseSlots = slotComputation.pastCloseSlots;
  const apptDurationMin = slotComputation.apptDurationMin;
  const closingMin = slotComputation.closingMin;

  // Combined duration string for the booking record (service + add-ons).
  // Falls back to the raw service duration if nothing parses or no add-ons selected.
  const computedBookingDuration = (() => {
    if (!selectedService) return "";
    const parseDur = (s: string): number => {
      if (!s) return 0;
      const lower = s.toLowerCase();
      let total = 0;
      const hr = lower.match(/(\d+(?:\.\d+)?)\s*(?:h|hr|hour)/);
      if (hr) total += parseFloat(hr[1]) * 60;
      const mn = lower.match(/(\d+)\s*(?:m|min)/);
      if (mn) total += parseInt(mn[1], 10);
      if (total === 0) {
        const bare = lower.match(/^\s*(\d+)\s*$/);
        if (bare) total = parseInt(bare[1], 10);
      }
      return total;
    };
    const totalMin =
      parseDur(selectedService.duration) +
      selectedAddons.reduce((sum, a) => sum + parseDur(a.duration), 0);
    if (totalMin <= 0) return selectedService.duration;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h > 0 && m > 0) return `${h}hr ${m}min`;
    if (h > 0) return `${h}hr`;
    return `${m}min`;
  })();

  // Combine service name with selected add-ons for the booking title
  // e.g. "Solid Full Color + Tier 2 Nail Art + Chrome"
  const serviceNameWithAddons = (() => {
    if (!selectedService) return "";
    if (selectedAddons.length === 0) return selectedService.name;
    return `${selectedService.name} + ${selectedAddons.map((a) => a.name).join(" + ")}`;
  })();



  // Format minutes-from-midnight to "h:mm AM/PM"
  const minutesToLabel = (m: number): string => {
    let h = Math.floor(m / 60);
    const mm = m % 60;
    const period = h >= 12 ? "PM" : "AM";
    if (h === 0) h = 12;
    else if (h > 12) h -= 12;
    return mm === 0 ? `${h}:00 ${period}` : `${h}:${String(mm).padStart(2, "0")} ${period}`;
  };
  const formatDuration = (m: number): string => {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    if (h && mm) return `${h}h ${mm}min`;
    if (h) return `${h}h`;
    return `${mm}min`;
  };

  // Clear selected time if it's no longer in the available slots.
  // Keep it selected when it's only blocked by closing time so the banner can explain why.
  useEffect(() => {
    if (
      selectedTime &&
      !availableTimeSlots.includes(selectedTime) &&
      !pastCloseSlots.includes(selectedTime)
    ) {
      setSelectedTime(null);
    }
  }, [selectedDate, availableTimeSlots, pastCloseSlots, selectedTime]);

  useEffect(() => {
    if (!user || adminMode) return;
    const fetchCoupons = async () => {
      const { data } = await supabase
        .from("coupons")
        .select("id, code, discount_amount, expires_at")
        .eq("user_id", user.id)
        .eq("is_used", false)
        .order("discount_amount", { ascending: false });
      const active = (data || []).filter((c: any) => new Date(c.expires_at) >= new Date());
      setAvailableCoupons(active);
    };
    fetchCoupons();
  }, [user, adminMode]);

  // Check if first booking for this user + auto-apply signup referral code
  useEffect(() => {
    const checkFirstBooking = async () => {
      const uid = checkUserId;
      if (!uid) return;
      const { data, count } = await supabase
        .from("bookings")
        .select("id", { count: "exact" }).is("deleted_at", null)
        .eq("user_id", uid);
      const total = count ?? data?.length ?? 0;
      setBookingCount(total);
      const isFirst = total === 0;
      setIsFirstBooking(isFirst);


      // Auto-apply signup referral code on first booking
      if (isFirst && !adminMode) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("signup_referral_code")
          .eq("user_id", uid)
          .maybeSingle();
        if (profile?.signup_referral_code) {
          // Validate the referral code exists and isn't the user's own
          const { data: referrerProfile } = await supabase
            .from("profiles")
            .select("user_id, full_name")
            .eq("referral_code", profile.signup_referral_code)
            .maybeSingle();
          if (referrerProfile && referrerProfile.user_id !== uid) {
            setReferralCodeInput(profile.signup_referral_code);
            setReferralApplied(true);
            toast.success(`Referral from ${referrerProfile.full_name || "a friend"} auto-applied!`);
          }
        }
      }
    };
    checkFirstBooking();
  }, [checkUserId, adminMode]);

  useEffect(() => {
    if (!user) return;
    if (isAdmin) {
      setBlockLoading(false);
      return;
    }
    const checkBlock = async () => {
      const { data: profile } = await supabase
        .from("profiles")
        .select("booking_count_reset_at")
        .eq("user_id", user.id)
        .maybeSingle();

      let query = supabase
        .from("bookings")
        .select("id").is("deleted_at", null)
        .eq("user_id", user.id)
        .eq("status", "cancelled");

      if (profile?.booking_count_reset_at) {
        query = query.gte("created_at", profile.booking_count_reset_at);
      }

      const { data: bookingData } = await query;
      setBookingBlocked((bookingData?.length || 0) >= 3);
      setBlockLoading(false);
    };
    checkBlock();
  }, [user, isAdmin]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/auth" replace />;

  const uploadInspirationPhotos = async (files: FileList | null) => {
    if (!files || files.length === 0 || !user) return;
    const targetUserId = adminMode && selectedClient ? selectedClient.user_id : user.id;
    setInspirationUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name} is not an image`);
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is too large (max 10MB)`);
          continue;
        }
        const ext = file.name.split(".").pop() || "jpg";
        const path = `${targetUserId}/inspiration/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const { error: uploadErr } = await supabase.storage
          .from("client-photos")
          .upload(path, file, { contentType: file.type, upsert: false });
        if (uploadErr) {
          toast.error(`Upload failed: ${uploadErr.message}`);
          continue;
        }
        const { data: row, error: insertErr } = await supabase
          .from("client_photos")
          .insert({
            user_id: targetUserId,
            category: "inspiration",
            file_path: path,
            uploaded_by: adminMode ? "admin" : "client",
          })
          .select("id, file_path")
          .single();
        if (insertErr || !row) {
          await supabase.storage.from("client-photos").remove([path]);
          toast.error("Could not save the photo. Please try again.");
          continue;
        }
        const { data: signed } = await supabase.storage
          .from("client-photos")
          .createSignedUrl(row.file_path, 60 * 60);
        setInspirationPhotos((prev) => [
          ...prev,
          { id: row.id, file_path: row.file_path, url: signed?.signedUrl || "" },
        ]);
      }
    } finally {
      setInspirationUploading(false);
      if (inspirationInputRef.current) inspirationInputRef.current.value = "";
    }
  };

  const removeInspirationPhoto = async (photo: { id: string; file_path: string }) => {
    await supabase.from("client_photos").delete().eq("id", photo.id);
    await supabase.storage.from("client-photos").remove([photo.file_path]);
    setInspirationPhotos((prev) => prev.filter((p) => p.id !== photo.id));
  };

  const handleConfirm = async () => {
    if (!selectedService || !selectedCategory || !selectedDate || !selectedTime) return;
    if (!adminMode && !confirmAcknowledged) return;
    if (adminMode && !selectedClient) {
      toast.error("Please select a client for this booking");
      return;
    }
    setSubmitting(true);

    const addonsTotal = selectedAddons.reduce((sum, a) => sum + a.price, 0);
    const totalPrice = selectedService.price + addonsTotal;
    const addonNotesParts: string[] = [];
    if (selectedAddons.length > 0) {
      addonNotesParts.push(`Add-ons: ${selectedAddons.map(a => `${a.name} (+$${a.price})`).join(", ")}`);
    }
    if (inspirationPhotos.length > 0) {
      addonNotesParts.push(`Inspiration photos: ${inspirationPhotos.length}`);
    }
    const addonNotes = addonNotesParts.length > 0 ? addonNotesParts.join(" | ") : null;
    const bookingUserId = adminMode && selectedClient ? selectedClient.user_id : user.id;
    const bookingDateStr = toLocalDateStr(selectedDate);

    // Records a dropped booking attempt so admins can see where clients fall out.
    const recordFailure = (stage: string, reason: string, errorCode?: string | null) =>
      logBookingFailure({
        userId: bookingUserId,
        clientName: adminMode && selectedClient
          ? selectedClient.full_name
          : ((user.user_metadata?.full_name as string | undefined) || null),
        clientEmail: adminMode && selectedClient ? selectedClient.email : (user.email || null),
        clientPhone: adminMode && selectedClient ? selectedClient.phone : null,
        bookingDate: bookingDateStr,
        bookingTime: selectedTime,
        serviceName: serviceNameWithAddons,
        serviceCategory: selectedCategory.title,
        addons: selectedAddons.map((a) => a.name),
        price: totalPrice,
        stage,
        reason,
        errorCode: errorCode ?? null,
        metadata: { admin_mode: adminMode, payment_type: paymentType, is_reschedule: isReschedule },
      });



    // FAIL-SAFE: re-check blocked dates/times at submit time. A stale tab could
    // otherwise submit a slot that an admin blocked after the page loaded.
    if (!adminMode) {
      const { data: freshBlocks } = await supabase
        .from("blocked_dates")
        .select("all_day, start_time, end_time")
        .eq("blocked_date", bookingDateStr);

      if (freshBlocks && freshBlocks.length > 0) {
        const startMin = timeToMinutes(selectedTime);
        const hhmm = (v: string | null) => {
          if (!v) return null;
          const [h, m] = v.split(":").map(Number);
          return h * 60 + (m || 0);
        };
        const blocked = freshBlocks.some((b: any) => {
          if (b.all_day) return true;
          const bs = hhmm(b.start_time);
          const be = hhmm(b.end_time);
          if (bs == null || be == null) return false;
          return startMin >= bs && startMin < be;
        });
        if (blocked) {
          setSubmitting(false);
          recordFailure("blocked_slot", "Date/time was blocked by admin before submit");
          toast.error("That time is no longer available", {
            description: "This date or time was just blocked. Please pick another slot.",
          });

          setStep(1);
          setSelectedTime("");
          return;
        }
      }
    }


    // Log SMS consent for Twilio A2P 10DLC compliance (only for client-driven bookings)
    if (!adminMode && confirmAcknowledged) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("phone")
        .eq("user_id", user.id)
        .maybeSingle();
      if (profile?.phone) {
        logSmsConsent({
          userId: user.id,
          phone: profile.phone,
          source: "booking_acknowledgment",
          consentText: SMS_CONSENT_TEXT.bookingAcknowledgment,
        });
      }
    }

    // Helper to send confirmation notifications
    const sendConfirmationNotifications = async (bookingData?: { id: string }) => {
      const clientEmail = adminMode && selectedClient ? selectedClient.email : user.email;
      const clientUserId = adminMode && selectedClient ? selectedClient.user_id : user.id;

      // Fetch referral code for the client
      let clientReferralCode: string | undefined;
      if (clientUserId) {
        const { data: refProfile } = await supabase
          .from("profiles")
          .select("referral_code")
          .eq("user_id", clientUserId)
          .maybeSingle();
        clientReferralCode = refProfile?.referral_code || undefined;
      }

      if (clientEmail) {
        supabase.functions.invoke("send-booking-email", {
          body: {
            type: "confirmation",
            email: clientEmail,
            service_name: serviceNameWithAddons,
            service_category: selectedCategory.title,
            booking_date: bookingDateStr,
            booking_time: selectedTime,
            duration: computedBookingDuration,
            price: totalPrice,
            referral_code: clientReferralCode,
            booking_id: bookingData?.id,
          },
        }).catch((err) => console.error("Email send failed:", err));
      }


      const clientPhone = adminMode && selectedClient ? selectedClient.phone : null;
      if (clientPhone) {
        supabase.functions.invoke("send-booking-sms", {
          body: {
            type: "confirmation",
            phone: clientPhone,
            service_name: serviceNameWithAddons,
            booking_date: bookingDateStr,
            booking_time: selectedTime,
            duration: computedBookingDuration,
            price: selectedService.price,
          },
        }).catch((err) => console.error("SMS send failed:", err));
      } else if (!adminMode) {
        supabase
          .from("profiles")
          .select("phone")
          .eq("user_id", user.id)
          .maybeSingle()
          .then(({ data: profile }) => {
            if (profile?.phone) {
              supabase.functions.invoke("send-booking-sms", {
                body: {
                  type: "confirmation",
                  phone: profile.phone,
                  service_name: serviceNameWithAddons,
                  booking_date: bookingDateStr,
                  booking_time: selectedTime,
                  duration: computedBookingDuration,
                  price: selectedService.price,
                },
              }).catch((err) => console.error("SMS send failed:", err));
            }
          });
      }

      // Outlook calendar sync (no-op when admin hasn't enabled it)
      try {
        const { data: createdBooking } = await supabase
          .from("bookings")
          .select("id").is("deleted_at", null)
          .eq("user_id", clientUserId!)
          .eq("booking_date", bookingDateStr)
          .eq("booking_time", selectedTime)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (createdBooking?.id) {
          const { syncBookingToOutlook } = await import("@/lib/outlookSync");
          const clientName = adminMode && selectedClient
            ? selectedClient.full_name
            : (user.user_metadata?.full_name as string | undefined);
          await syncBookingToOutlook(
            {
              id: createdBooking.id,
              service_name: serviceNameWithAddons,
              service_category: selectedCategory.title,
              booking_date: bookingDateStr,
              booking_time: selectedTime,
              duration: computedBookingDuration,
              status: "confirmed",
              client_name: clientName,
              client_email: clientEmail || undefined,
              client_phone: clientPhone || undefined,
            },
            "created",
          );
        }
      } catch (err) {
        console.warn("Outlook sync skipped:", err);
      }
    };

    // Helper: mark a coupon as redeemed (promotional → atomic increment, personal → set is_used)
    const redeemCoupons = async (
      couponsToRedeem: { id: string; is_promotional?: boolean }[]
    ) => {
      await Promise.all(
        couponsToRedeem.map(async (c) => {
          if (c.is_promotional) {
            await supabase.rpc("redeem_promotional_coupon", { _coupon_id: c.id });
          } else {
            await supabase
              .from("coupons")
              .update({ is_used: true, used_at: new Date().toISOString() })
              .eq("id", c.id);
          }
        })
      );
    };

    // Helper: persist selectedAddons to booking_addons (skip_rollup=true since
    // service_name / price / duration already include them on the parent booking).
    const persistAddons = async (bookingId: string) => {
      if (!selectedAddons.length || !bookingId) return;
      const rows = selectedAddons.map((a) => ({
        booking_id: bookingId,
        user_id: bookingUserId,
        addon_id: a.id,
        name: a.name,
        price: a.price,
        duration: a.duration,
        status: "confirmed" as const,
        requested_by: adminMode ? "admin" : "client",
        requested_by_user: user.id,
        confirmed_by: user.id,
        skip_rollup: true,
      }));
      const { error: addonErr } = await supabase.from("booking_addons").insert(rows);
      if (addonErr) console.error("Failed to persist addons:", addonErr);
    };

    if (adminMode) {
      const insertAdminBooking = (override: boolean) =>
        supabase.from("bookings").insert({
          user_id: bookingUserId,
          service_name: serviceNameWithAddons,
          service_category: selectedCategory.title,
          booking_date: bookingDateStr,
          booking_time: selectedTime,
          duration: computedBookingDuration,
          price: totalPrice,
          status: "confirmed",
          payment_status: "paid",
          payment_type: "admin",
          notes: addonNotes,
          created_by_admin: true,
          blocked_day_override: override,
        }).select("id").single();

      let { data: inserted, error } = await insertAdminBooking(false);

      if (error && /too close to another appointment|unavailable for booking/i.test(error.message)) {
        setSubmitting(false);
        recordFailure("insert_admin", error.message, error.code);
        const confirmed = window.confirm(
          `${error.message}\n\nDouble-book this time anyway?`
        );
        if (!confirmed) return;
        setSubmitting(true);
        ({ data: inserted, error } = await insertAdminBooking(true));
      }

      if (error) {
        setSubmitting(false);
        recordFailure("insert_admin", error.message, error.code);
        toast.error("Failed to book. Please try again.");
        return;
      }

      await persistAddons(inserted!.id);

      sendConfirmationNotifications({ id: inserted!.id });



      // Mark all applied coupons as used
      if (appliedCoupons.length > 0) {
        await redeemCoupons(appliedCoupons);
      }

      // Record referral entry
      if (referralApplied && referralCodeInput) {
        const { data: referrerProfile } = await supabase
          .from("profiles")
          .select("user_id")
          .eq("referral_code", referralCodeInput.toUpperCase())
          .maybeSingle();
        if (referrerProfile) {
          await supabase.from("referral_entries").insert({
            referrer_user_id: referrerProfile.user_id,
            referred_user_id: bookingUserId,
          });
        }
      }

      setSubmitting(false);
      toast.success("Appointment booked!");
      setBookingComplete(true);
      return;
    }

    // Determine the actual amount to charge.
    // For "full" payments, charge the SERVICE PRICE + ADD-ONS TOTAL minus any coupons.
    const couponDiscount = appliedCoupons.reduce((sum, c) => sum + c.discount_amount, 0);
    const fullChargeBase = (selectedService.price || 0) + addonsTotal;
    const chargeAmount = !effectiveDepositEnabled ? 0 : (paymentType === "deposit" ? computedDeposit : Math.max(0, fullChargeBase - couponDiscount));

    // If amount is $0 (e.g. deposit disabled, $0 deposit, or fully covered by coupons), skip Stripe
    if (chargeAmount <= 0) {
      try {
        // For reschedules, look up the original booking's deposit so it carries over
        let carriedDeposit = 0;
        if (isReschedule && originalBookingId) {
          const { data: orig } = await supabase
            .from("bookings")
            .select("deposit_paid_amount").is("deleted_at", null)
            .eq("id", originalBookingId)
            .maybeSingle();
          carriedDeposit = Number(orig?.deposit_paid_amount || 0);
        }

        // Reschedule flow: create the new booking as `pending` (admin must
        // confirm) and tag it with reschedule_requested_at so the 24h
        // reminder cron can pick it up.
        const insertPayload: any = {
          user_id: bookingUserId,
          service_name: serviceNameWithAddons,
          service_category: selectedCategory.title,
          booking_date: bookingDateStr,
          booking_time: selectedTime,
          duration: computedBookingDuration,
          price: totalPrice,
          status: isReschedule ? "pending" : "confirmed",
          payment_status: isReschedule
            ? "deposit_paid" // existing deposit carries over from the original booking
            : (!effectiveDepositEnabled ? "pending" : (paymentType === "full" ? "paid" : "deposit_paid")),
          payment_type: isReschedule ? "deposit" : paymentType,
          discount_applied: couponDiscount > 0 ? couponDiscount : null,
          notes: addonNotes,
          ...(isReschedule ? { reschedule_requested_at: new Date().toISOString(), deposit_paid_amount: carriedDeposit } : {}),
        };

        const { data: insertedFree, error } = await supabase
          .from("bookings")
          .insert(insertPayload)
          .select("id")
          .single();
        if (error) throw error;
        await persistAddons(insertedFree!.id);


        if (isReschedule) {
          // Cancel the original booking now that the new pending one exists
          if (originalBookingId) {
            await supabase
              .from("bookings")
              .update({ status: "cancelled" })
              .eq("id", originalBookingId);
          }
          // Send the client a "reschedule pending" email (no confirmation
          // yet — admin must confirm).
          const clientEmail = user.email;
          if (clientEmail) {
            const remaining = Math.max(0, totalPrice - carriedDeposit);
            supabase.functions.invoke("send-booking-email", {
              body: {
                type: "reschedule_pending",
                email: clientEmail,
                service_name: serviceNameWithAddons,
                service_category: selectedCategory.title,
                booking_date: bookingDateStr,
                booking_time: selectedTime,
                duration: computedBookingDuration,
                price: totalPrice,
                deposit_paid: carriedDeposit,
                remaining_balance: remaining,
                booking_id: insertedFree!.id,
              },
            }).catch((err) => console.error("Reschedule pending email failed:", err));
          }
        } else {
          sendConfirmationNotifications({ id: insertedFree!.id });

        }

        if (appliedCoupons.length > 0) {
          await redeemCoupons(appliedCoupons);
        }
        if (referralApplied && referralCodeInput) {
          const { data: referrerProfile } = await supabase
            .from("profiles")
            .select("user_id")
            .eq("referral_code", referralCodeInput.toUpperCase())
            .maybeSingle();
          if (referrerProfile) {
            await supabase.from("referral_entries").insert({
              referrer_user_id: referrerProfile.user_id,
              referred_user_id: bookingUserId,
            });
          }
        }

        setSubmitting(false);
        toast.success(isReschedule ? "Reschedule submitted — awaiting admin confirmation" : "Appointment booked!");
        setBookingComplete(true);
        return;
      } catch (err: any) {
        setSubmitting(false);
        recordFailure("insert_no_payment", err?.message || String(err), err?.code);
        toast.error(err.message || "Failed to book. Please try again.");
        return;
      }
    }

    // Client bookings: redirect to Stripe first, booking created AFTER payment
    setProcessingPayment(true);
    try {
      const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke("create-checkout", {
       body: {
          paymentType,
          serviceId: selectedService.id,
          serviceName: serviceNameWithAddons,
          serviceCategory: selectedCategory.title,
          depositAmount: computedDeposit,
          bookingDate: bookingDateStr,
          bookingTime: selectedTime,
          duration: computedBookingDuration,
          userId: bookingUserId,
          couponIds: appliedCoupons.map(c => c.id),
          addonsTotal,
          addons: selectedAddons.map((a) => ({
            addon_id: a.id, name: a.name, price: a.price, duration: a.duration,
          })),
        },
      });

      if (checkoutError || (!checkoutData?.url && !checkoutData?.free)) {
        throw new Error(checkoutData?.error || "Failed to create checkout session");
      }

      if (checkoutData?.free) {
        // Fully covered — create booking directly
        const { data: insertedPaid, error } = await supabase.from("bookings").insert({
          user_id: bookingUserId,
          service_name: serviceNameWithAddons,
          service_category: selectedCategory.title,
          booking_date: bookingDateStr,
          booking_time: selectedTime,
          duration: computedBookingDuration,
          price: totalPrice,
          status: "confirmed",
          payment_status: "paid",
          payment_type: paymentType,
          discount_applied: couponDiscount > 0 ? couponDiscount : null,
          notes: addonNotes,
        }).select("id").single();
        if (error) throw error;
        await persistAddons(insertedPaid!.id);
        sendConfirmationNotifications({ id: insertedPaid!.id });

        const validatedIds: string[] = checkoutData.validatedCouponIds || appliedCoupons.map(c => c.id);
        if (validatedIds.length > 0) {
          // Match validated IDs back to their applied entries to know promo vs personal
          const toRedeem = appliedCoupons.filter(c => validatedIds.includes(c.id));
          await redeemCoupons(toRedeem.length > 0 ? toRedeem : validatedIds.map(id => ({ id })));
        }
        setProcessingPayment(false);
        setSubmitting(false);
        toast.success("Appointment booked!");
        setBookingComplete(true);
        return;
      }

      // Mark all applied coupons as used before redirecting to payment
      if (appliedCoupons.length > 0) {
        await redeemCoupons(appliedCoupons);
      }

      // Record referral entry before redirecting
      if (referralApplied && referralCodeInput) {
        const { data: referrerProfile } = await supabase
          .from("profiles")
          .select("user_id")
          .eq("referral_code", referralCodeInput.toUpperCase())
          .maybeSingle();
        if (referrerProfile) {
          await supabase.from("referral_entries").insert({
            referrer_user_id: referrerProfile.user_id,
            referred_user_id: bookingUserId,
          });
        }
      }

      window.location.href = checkoutData.url;
    } catch (err: any) {
      setProcessingPayment(false);
      setSubmitting(false);
      recordFailure("checkout", err?.message || String(err), err?.code);
      toast.error(err.message || "Payment processing failed. Please try again.");
    }
  };

  // Photo requirement checks
  const isPedicureService = selectedCategory?.title?.toLowerCase().includes("pedicure");
  const needsPhotos = (() => {
    if (adminMode) return false; // Admin bypasses
    if (isFirstBooking && photoSettings.requireFirst) return true;
    if (!isFirstBooking && photoSettings.requireSubsequent) return true;
    if (isPedicureService && photoSettings.requirePedicure) return true;
    return false;
  })();

  const missingPhotos: string[] = [];
  if (needsPhotos) {
    if (!hasPhoto("hands")) missingPhotos.push("Hands");
    if (!hasPhoto("face")) missingPhotos.push("Face");
    if (isPedicureService && photoSettings.requirePedicure && !hasPhoto("feet")) {
      missingPhotos.push("Feet");
    }
  }

  const photoBlocked = needsPhotos && missingPhotos.length > 0 && photoSettings.enforcement === "block" && !photoWarningDismissed;

  // Returning-client rebook: prefill the previous service (+ add-ons) and either
  // jump straight to date/time or drop them on step 0 to edit.
  const handleRebook = async (b: PastBooking, wantsChanges: boolean) => {
    const match = resolveRebookService(services, b);
    if (!match) return;
    const cat = match.category as typeof services[number];
    const sub = match.sub;

    setSelectedCategory(cat);
    setSelectedService(sub);
    setSelectedTime(null);
    setPoliciesAcknowledged(true);

    let addons: ServiceAddon[] = [];
    if (b.addonNames.length) {
      const { data } = await supabase
        .from("service_addons")
        .select("*")
        .eq("category_id", cat.id);
      addons = ((data || []) as ServiceAddon[]).filter((a) => b.addonNames.includes(a.name));
    }
    setSelectedAddons(addons);

    // Re-run the photo requirement gate for the rebooked service — the one-tap
    // shortcut must not bypass enforcement="block".
    const rebookIsPedicure = !!cat?.title?.toLowerCase().includes("pedicure");
    const rebookNeedsPhotos =
      !adminMode &&
      ((isFirstBooking && photoSettings.requireFirst) ||
        (!isFirstBooking && photoSettings.requireSubsequent) ||
        (rebookIsPedicure && photoSettings.requirePedicure));
    const rebookMissing: string[] = [];
    if (rebookNeedsPhotos) {
      if (!hasPhoto("hands")) rebookMissing.push("Hands");
      if (!hasPhoto("face")) rebookMissing.push("Face");
      if (rebookIsPedicure && photoSettings.requirePedicure && !hasPhoto("feet")) rebookMissing.push("Feet");
    }
    const rebookBlocked =
      rebookMissing.length > 0 && photoSettings.enforcement === "block" && !photoWarningDismissed;

    if (wantsChanges || rebookBlocked) {
      if (rebookBlocked) {
        toast.error(`Please upload your ${rebookMissing.join(", ")} photo${rebookMissing.length > 1 ? "s" : ""} before booking.`);
      }
      setTimeout(() => {
        const target = rebookBlocked
          ? subServicesRef.current
          : addonsRef.current || subServicesRef.current;
        target?.scrollIntoView({ behavior: "smooth", block: "start" });

      }, 150);
    } else {
      setStep(1);
    }
  };


  const canProceedToStep2 = selectedCategory && selectedService && (adminMode || policiesAcknowledged) && !photoBlocked;
  const canProceedToStep3 = selectedDate && selectedTime;

  if (blockLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <section className="pt-32 pb-24 text-center">
          <p className="text-muted-foreground text-sm">Loading…</p>
        </section>
        <Footer />
      </div>
    );
  }

  if (bookingBlocked && !adminMode) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <section className="pt-32 pb-24">
          <div className="container mx-auto px-6 max-w-lg text-center">
            <div className="bg-destructive/10 border border-destructive/30 p-10">
              <X size={48} className="mx-auto text-destructive mb-4" />
              <h1 className="font-serif text-3xl font-bold italic mb-4 text-destructive">Booking Limit Reached</h1>
              <p className="text-muted-foreground font-light leading-relaxed mb-6">
                You have reached the maximum of 3 active bookings. Please contact us to manage your existing appointments before scheduling new ones.
              </p>
              <Button asChild variant="outline" className="rounded-none border-primary/30 hover:bg-primary/10 tracking-[0.2em] uppercase text-xs font-medium">
                <Link to="/account">View My Appointments</Link>
              </Button>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const bookCategoryParam = (searchParams.get("category") || "").toLowerCase();
  const bookOg = BOOK_OG[bookCategoryParam];
  const bookSeoPath = `/book${bookCategoryParam ? `?category=${bookCategoryParam}` : ""}`;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        path={bookSeoPath}
        title={bookOg?.title ?? "Book Your Appointment — In Paradise Beauty"}
        description={bookOg?.description ?? "Choose your service and appointment time for nails, lashes, pedicures or skincare at In Paradise Beauty."}
        image={bookOg?.image}
      />
      <Navbar />
      <BookingsOpenBanner />
      <section className="pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="text-center mb-10">
            <p className="text-primary text-xs font-light tracking-[0.5em] uppercase mb-3">
              {adminMode ? "Admin Booking" : "Book an Appointment"}
            </p>
            <h1 className="font-serif text-4xl md:text-5xl font-bold italic">
              {steps[step]}
            </h1>
          </div>

          {/* Admin client selector */}
          {adminMode && step === 0 && (
            <div className="mb-8">
              <h3 className="text-xs tracking-[0.2em] uppercase font-medium text-muted-foreground mb-3 flex items-center gap-2">
                <UserPlus size={14} /> Select Client
              </h3>
              <ClientSearch selectedClient={selectedClient} onSelectClient={setSelectedClient} />
            </div>
          )}

          {/* Returning client — one-tap rebook */}
          {!adminMode && user && step === 0 && !bookingComplete && (
            <RebookBar userId={user.id} services={services} onRebook={handleRebook} />
          )}

          {/* Step indicators */}

          <div className="flex items-center justify-center gap-2 mb-12">
            {steps.map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                  i <= step ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                }`}>
                  {i < step ? <Check size={14} /> : i + 1}
                </div>
                {i < steps.length - 1 && (
                  <div className={`w-12 h-0.5 ${i < step ? "bg-primary" : "bg-muted"}`} />
                )}
              </div>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {bookingComplete && selectedService && selectedCategory && selectedDate && selectedTime ? (
              <motion.div key="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
                <div className="bg-card border border-primary/30 p-10 max-w-lg mx-auto">
                  <PartyPopper size={48} className="text-primary mx-auto mb-4" />
                  <h2 className="font-serif text-3xl font-bold italic mb-2">You're Booked!</h2>
                  <p className="text-muted-foreground font-light mb-6">
                    {selectedService.name} on {new Intl.DateTimeFormat("en-US", { dateStyle: "long" }).format(selectedDate)} at {selectedTime}
                  </p>
                  <div className="mb-6">
                    <AddToCalendarButtons
                      booking={{
                        service_name: serviceNameWithAddons,
                        service_category: selectedCategory.title,
                        booking_date: toLocalDateStr(selectedDate),
                        booking_time: selectedTime,
                        duration: computedBookingDuration,
                      }}
                    />
                  </div>
                  <Button
                    onClick={() => navigate(adminMode ? "/admin/bookings" : "/account")}
                    className="rounded-none px-10 py-5 text-xs font-medium tracking-[0.3em] uppercase border border-primary hover:bg-primary/90"
                  >
                    {adminMode ? "View All Bookings" : "View My Appointments"}
                  </Button>
                </div>
              </motion.div>
            ) : (
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="step0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                {/* Category select */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  {services.map((cat) => (
                    <button
                      key={cat.id}
                    onClick={() => {
                      setSelectedCategory(cat);
                      setSelectedService(null);
                      setSelectedAddons([]);
                      setTimeout(() => subServicesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
                    }}
                      className={`p-6 border text-center transition-all ${
                        selectedCategory?.id === cat.id
                          ? "border-primary bg-primary/10"
                          : "border-border bg-card hover:border-primary/50"
                      }`}
                    >
                      <span className="text-3xl block mb-2">{cat.icon}</span>
                      <span className="text-xs font-medium tracking-[0.2em] uppercase">{cat.title}</span>
                    </button>
                  ))}
                </div>

                {/* Sub-services */}
                {selectedCategory && (
                  <div ref={subServicesRef} className="space-y-3 mb-10">
                    {selectedCategory.subServices.map((sub) => (
                      <button
                        key={sub.name}
                        onClick={() => {
                          setSelectedService(sub);
                          setTimeout(() => {
                            const target = addonsRef.current || policiesRef.current;
                            target?.scrollIntoView({ behavior: "smooth", block: "start" });
                          }, 150);
                        }}
                        className={`w-full text-left p-5 border transition-all flex justify-between items-center ${
                          selectedService?.name === sub.name
                            ? "border-primary bg-primary/10"
                            : "border-border bg-card hover:border-primary/50"
                        }`}
                      >
                        <div>
                          <h3 className="font-semibold">{sub.name}</h3>
                          <p className="text-muted-foreground text-sm font-light">{sub.description}</p>
                        </div>
                        <div className="text-right ml-4 shrink-0">
                          <p className="font-semibold text-primary">${sub.price}</p>
                          <p className="text-muted-foreground text-xs">{sub.duration}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Add-ons */}
                {selectedCategory && selectedService && availableAddons.length > 0 && (
                  <motion.div ref={addonsRef}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-10"
                  >
                    <h3 className="font-serif text-xl font-semibold italic mb-4 flex items-center gap-2">
                      <Plus size={18} className="text-primary" /> Add-Ons
                    </h3>
                    <p className="text-xs text-muted-foreground mb-3 tracking-wide">Select any add-ons for your service (optional)</p>
                    <div className="space-y-2">
                      {availableAddons.map((addon) => {
                        const isSelected = selectedAddons.some(a => a.id === addon.id);
                        return (
                          <button
                            key={addon.id}
                            onClick={() => {
                              setSelectedAddons(prev =>
                                isSelected
                                  ? prev.filter(a => a.id !== addon.id)
                                  : [...prev, addon]
                              );
                            }}
                            className={`w-full text-left p-4 border transition-all flex justify-between items-center ${
                              isSelected
                                ? "border-primary bg-primary/10"
                                : "border-border bg-card hover:border-primary/50"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                                isSelected ? "bg-primary border-primary" : "border-muted-foreground/40"
                              }`}>
                                {isSelected && <Check size={12} className="text-primary-foreground" />}
                              </div>
                              <span className="text-sm font-medium">{addon.name}</span>
                            </div>
                            <div className="text-right ml-4 shrink-0">
                              <p className="text-sm font-semibold text-primary">+${addon.price}</p>
                              <p className="text-muted-foreground text-xs">+{addon.duration}</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {selectedAddons.length > 0 && (
                      <div className="mt-3 p-3 bg-primary/5 border border-primary/20 flex justify-between items-center">
                        <span className="text-xs text-muted-foreground">{selectedAddons.length} add-on(s) selected</span>
                        <span className="text-sm font-semibold text-primary">+${selectedAddons.reduce((sum, a) => sum + a.price, 0)}</span>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Policies - shown after selecting a service (NOT for admin) */}
                {selectedService && !adminMode && (
                  <motion.div
                    ref={policiesRef}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                  >
                    <div className="border-t border-border/50 pt-10 mb-6">
                      <PoliciesSection policies={policies} />
                    </div>

                    <div className="flex items-start gap-3 mt-6 p-4 border border-border/50 bg-card">
                      <Checkbox
                        id="policies-ack"
                        checked={policiesAcknowledged}
                        onCheckedChange={(checked) => setPoliciesAcknowledged(checked === true)}
                        className="mt-0.5"
                      />
                      <label htmlFor="policies-ack" className="text-sm font-light text-muted-foreground leading-relaxed cursor-pointer">
                        I have read and agree to the <Link to="/cancellation-policy" className="text-primary underline hover:text-primary/80" target="_blank">booking policies</Link>, including the cancellation, deposit, and late policy terms. I also consent to receive recurring <Link to="/sms-consent" className="text-primary underline hover:text-primary/80" target="_blank">SMS & email communications</Link> from In Paradise Beauty about my appointments. Msg & data rates may apply. Reply STOP to opt out, HELP for help.
                      </label>
                    </div>
                  </motion.div>
                )}

                {/* Photo requirement warning */}
                {selectedService && !adminMode && needsPhotos && missingPhotos.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`mb-6 p-4 border ${photoSettings.enforcement === "block" ? "border-destructive/50 bg-destructive/10" : "border-yellow-500/50 bg-yellow-500/10"}`}
                  >
                    <div className="flex items-start gap-3">
                      {photoSettings.enforcement === "block" ? (
                        <Camera size={20} className="text-destructive mt-0.5 shrink-0" />
                      ) : (
                        <AlertTriangle size={20} className="text-yellow-600 mt-0.5 shrink-0" />
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {photoSettings.enforcement === "block" ? "Photos Required" : "Photos Recommended"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Please upload the following photos on your{" "}
                          <Link to="/account" className="text-primary underline">Account page</Link>
                          {" "}before booking: <strong>{missingPhotos.join(", ")}</strong>
                        </p>
                        {photoSettings.enforcement === "warn" && !photoWarningDismissed && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPhotoWarningDismissed(true)}
                            className="mt-2 text-xs text-muted-foreground"
                          >
                            Continue without photos
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}

                <div className="flex justify-end mt-8">
                  <Button
                    onClick={() => {
                      const isLashes = selectedCategory?.title?.toLowerCase().includes("lash");
                      const serviceKey = `${selectedCategory?.id}:${selectedService?.name}`;
                      if (
                        isLashes &&
                        !adminMode &&
                        inspirationPhotos.length === 0 &&
                        lashInspoPromptedFor !== serviceKey
                      ) {
                        setLashInspoPromptedFor(serviceKey);
                        setLashInspoOpen(true);
                        return;
                      }
                      setStep(1);
                    }}
                    disabled={!canProceedToStep2 || (adminMode && !selectedClient)}
                    className="rounded-none px-10 py-5 text-xs font-medium tracking-[0.3em] uppercase border border-primary hover:bg-primary/90"
                  >
                    Next
                  </Button>
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="flex flex-col items-center gap-3">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={adminMode ? undefined : isDateDisabled}
                      fromDate={adminMode ? undefined : new Date()}
                      className="rounded-none border border-border bg-card p-4"
                      today={new Date()}
                    />
                    <div className="flex items-center gap-4 text-[10px] tracking-[0.2em] uppercase text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <span className="inline-block w-2 h-2 rounded-full bg-muted-foreground/40" />
                        Closed
                      </span>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-serif text-xl font-semibold mb-4 flex items-center gap-2 italic">
                      <Clock size={18} className="text-primary" /> Select Time
                      {selectedService && closingMin !== null && apptDurationMin > 0 && (
                        <span className="ml-auto text-[11px] not-italic font-normal tracking-wide uppercase text-muted-foreground">
                          Latest start: <span className="text-foreground font-medium">{minutesToLabel(closingMin - apptDurationMin)}</span>
                        </span>
                      )}
                    </h3>
                    {selectedService && closingMin !== null && selectedTime && pastCloseSlots.includes(selectedTime) && (
                      <div
                        role="alert"
                        className="mb-4 border border-destructive/40 bg-destructive/5 p-3 flex items-start gap-2 text-xs text-destructive"
                      >
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                        <p className="leading-relaxed">
                          Your selected appointment is{" "}
                          <span className="font-semibold">{formatDuration(apptDurationMin)}</span> long. Times after{" "}
                          <span className="font-semibold">{minutesToLabel(closingMin - apptDurationMin)}</span> would extend past the{" "}
                          <span className="font-semibold">{minutesToLabel(closingMin)}</span> closing time and cannot be booked.
                        </p>
                      </div>
                    )}
                    {selectedDate && (() => {
                      const ds = toLocalDateStr(selectedDate);
                      const fmt = (t: string) => {
                        const [hStr, mStr] = t.split(":");
                        let h = parseInt(hStr, 10);
                        const mm = mStr;
                        const period = h >= 12 ? "PM" : "AM";
                        if (h === 0) h = 12; else if (h > 12) h -= 12;
                        return mm === "00" ? `${h}:00 ${period}` : `${h}:${mm} ${period}`;
                      };
                      const dayBlocks = blockedDates.filter(
                        (b) => b.blocked_date === ds && !b.all_day && b.start_time && b.end_time,
                      );
                      if (dayBlocks.length === 0) return null;
                      return (
                        <div className="mb-4 border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
                          <p className="font-medium uppercase tracking-[0.2em] text-[10px] mb-1.5 text-foreground">
                            Unavailable on this day
                          </p>
                          <ul className="space-y-0.5">
                            {dayBlocks.map((b, i) => (
                              <li key={i}>
                                {fmt(b.start_time!.slice(0, 5))} – {fmt(b.end_time!.slice(0, 5))}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })()}
                    <div className="grid grid-cols-3 gap-2">
                      {availableTimeSlots.length > 0 ? availableTimeSlots.map((time) => (
                        <button
                          key={time}
                          onClick={() => setSelectedTime(time)}
                          className={`py-2.5 px-3 text-sm font-medium transition-all ${
                            selectedTime === time
                              ? "bg-primary text-primary-foreground"
                              : "bg-card border border-border hover:border-primary/50"
                          }`}
                        >
                          {time}
                        </button>
                      )) : (
                        <p className="col-span-3 text-sm text-muted-foreground text-center py-4">No available time slots for this date.</p>
                      )}
                      {pastCloseSlots.map((time) => (
                        <button
                          key={`past-${time}`}
                          type="button"
                          disabled
                          title={`Extends past ${minutesToLabel(closingMin!)} closing time`}
                          aria-label={`${time} unavailable — extends past closing`}
                          className="py-2.5 px-3 text-sm font-medium bg-muted/30 border border-dashed border-border text-muted-foreground line-through cursor-not-allowed opacity-60"
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex justify-between mt-8">
                  <Button variant="outline" onClick={() => setStep(0)} className="rounded-none px-8 border-primary/30 hover:bg-primary/10 tracking-[0.2em] uppercase text-xs font-medium">
                    <ArrowLeft size={14} /> Back
                  </Button>
                  <Button
                    onClick={() => setStep(2)}
                    disabled={!canProceedToStep3}
                    className="rounded-none px-10 py-5 text-xs font-medium tracking-[0.3em] uppercase border border-primary hover:bg-primary/90"
                  >
                    Next
                  </Button>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <div className="bg-card border border-border/50 p-8 max-w-lg mx-auto mb-8">
                  <h3 className="font-serif text-2xl font-bold italic mb-6 text-center">Booking Summary</h3>
                  <div className="space-y-4 mb-6">
                    {adminMode && selectedClient && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground text-sm">Client</span>
                        <span className="font-semibold">{selectedClient.full_name || "Unnamed"}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground text-sm">Service</span>
                      <span className="font-semibold">{selectedService?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground text-sm">Category</span>
                      <span className="capitalize">{selectedCategory?.title}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground text-sm flex items-center gap-1.5"><CalendarDays size={14} /> Date</span>
                      <span>{selectedDate && new Intl.DateTimeFormat("en-US", { dateStyle: "long" }).format(selectedDate)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground text-sm flex items-center gap-1.5"><Clock size={14} /> Time</span>
                      <span>{selectedTime}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground text-sm">Duration</span>
                      <span>{computedBookingDuration}</span>
                    </div>
                    {selectedAddons.length > 0 && (
                      <div className="border-t border-border pt-3 space-y-2">
                        <span className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Add-Ons</span>
                        {selectedAddons.map((addon) => (
                          <div key={addon.id} className="flex justify-between text-sm gap-4">
                            <span className="text-muted-foreground">{addon.name}</span>
                            <span className="text-primary whitespace-nowrap">+${addon.price} · +{addon.duration}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="border-t border-border pt-4 flex justify-between">
                      <span className="font-semibold text-lg">Total</span>
                      <div className="text-right">
                        {(() => {
                          const addonsTotal = selectedAddons.reduce((sum, a) => sum + a.price, 0);
                          const basePrice = (selectedService?.price || 0) + addonsTotal;
                          const couponDiscount = appliedCoupons.reduce((sum, c) => sum + c.discount_amount, 0);
                          if (couponDiscount > 0) {
                            return (
                              <>
                                <span className="text-muted-foreground line-through text-sm mr-2">${basePrice}</span>
                                <span className="font-semibold text-lg text-primary">
                                  ${Math.max(0, basePrice - couponDiscount)}
                                </span>
                              </>
                            );
                          }
                          return <span className="font-semibold text-lg text-primary">${basePrice}</span>;
                        })()}
                      </div>
                    </div>

                    {/* Deposit / Balance breakdown — only when paying a deposit at checkout */}
                    {!adminMode && effectiveDepositEnabled && computedDeposit > 0 && paymentType === "deposit" && !isReschedule && (() => {
                      const addonsTotal = selectedAddons.reduce((sum, a) => sum + a.price, 0);
                      const basePrice = (selectedService?.price || 0) + addonsTotal;
                      const remaining = Math.max(0, basePrice - computedDeposit);
                      return (
                        <div className="border-t border-dashed border-border/60 pt-3 mt-1 space-y-1.5 text-sm">
                          <div className="flex justify-between text-muted-foreground">
                            <span>Deposit due now</span>
                            <span className="font-semibold text-foreground">${computedDeposit.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Remaining balance</span>
                            <span className="font-semibold text-primary">${remaining.toFixed(2)}</span>
                          </div>
                          <p className="text-[11px] text-muted-foreground italic text-right">Due at your appointment</p>
                        </div>
                      );
                    })()}

                    {/* Coupon / Rewards Section */}
                    {!adminMode && (
                      <div className="border-t border-border pt-4 mt-2">
                        <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 flex items-center gap-2">
                          <Tag size={14} /> Coupons & Rewards
                        </p>
                        {/* Applied coupons list */}
                        {appliedCoupons.length > 0 && (
                          <div className="space-y-2 mb-3">
                            {appliedCoupons.map((c) => (
                              <div key={c.id} className="flex items-center justify-between p-3 border border-primary/30 bg-primary/5">
                                <div className="flex items-center gap-2">
                                  <Check size={14} className="text-primary" />
                                  <code className="font-mono font-bold text-xs tracking-widest">{c.code}</code>
                                  <span className="text-sm text-primary font-semibold">-${c.discount_amount}</span>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setAppliedCoupons(prev => prev.filter(ac => ac.id !== c.id))}
                                  className="text-xs text-muted-foreground"
                                >
                                  Remove
                                </Button>
                              </div>
                            ))}
                            <p className="text-xs text-primary font-semibold text-right">
                              Total discount: -${appliedCoupons.reduce((sum, c) => sum + c.discount_amount, 0)}
                            </p>
                          </div>
                        )}

                        {/* Available coupons (excluding already applied) */}
                        {(() => {
                          const appliedIds = new Set(appliedCoupons.map(c => c.id));
                          const remaining = availableCoupons.filter(c => !appliedIds.has(c.id));
                          return remaining.length > 0 ? (
                            <div className="mb-3 space-y-2">
                              <p className="text-xs text-muted-foreground mb-1">Your available coupons:</p>
                              {remaining.map((c) => (
                                <button
                                  key={c.id}
                                  onClick={() => {
                                    setAppliedCoupons(prev => [...prev, { code: c.code, discount_amount: c.discount_amount, id: c.id }]);
                                    toast.success(`Coupon ${c.code} applied! $${c.discount_amount} off`);
                                  }}
                                  className="w-full flex items-center justify-between p-3 border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-colors text-left"
                                >
                                  <div className="flex items-center gap-2">
                                    <Gift size={14} className="text-primary" />
                                    <code className="font-mono font-bold text-xs tracking-widest">{c.code}</code>
                                    <span className="text-sm text-primary font-semibold">${c.discount_amount} off</span>
                                  </div>
                                  <span className="text-[10px] tracking-[0.2em] uppercase text-primary font-medium">Tap to apply</span>
                                </button>
                              ))}
                            </div>
                          ) : null;
                        })()}

                        {/* Manual coupon code input */}
                        <div className="flex gap-2">
                          <Input
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                            placeholder="Enter coupon code"
                            className="bg-background border-border/50 font-mono tracking-widest uppercase max-w-[200px]"
                          />
                          <Button
                            variant="outline"
                            disabled={couponLoading || couponCode.length < 3}
                            onClick={async () => {
                              setCouponLoading(true);
                              try {
                                const codeUpper = couponCode.trim().toUpperCase();
                                if (appliedCoupons.some(c => c.code === codeUpper)) {
                                  toast.error("This coupon is already applied");
                                  setCouponLoading(false);
                                  return;
                                }
                                // Look up by code only — RLS allows the user to see their own
                                // personal coupons + active promotional coupons
                                const { data: coupons, error } = await supabase
                                  .from("coupons")
                                  .select("*")
                                  .eq("code", codeUpper);

                                if (error || !coupons || coupons.length === 0) {
                                  toast.error("Invalid coupon code");
                                  setCouponLoading(false);
                                  return;
                                }

                                // Prefer promotional, fall back to personal
                                const coupon: any =
                                  coupons.find((c: any) => c.is_promotional) ||
                                  coupons.find((c: any) => c.user_id === user.id);

                                if (!coupon) {
                                  toast.error("Invalid coupon code");
                                  setCouponLoading(false);
                                  return;
                                }

                                const now = new Date();
                                if (new Date(coupon.expires_at) < now) {
                                  toast.error("This coupon has expired");
                                  setCouponLoading(false);
                                  return;
                                }

                                if (coupon.is_promotional) {
                                  if (coupon.valid_from && new Date(coupon.valid_from) > now) {
                                    toast.error("This coupon is not active yet");
                                    setCouponLoading(false);
                                    return;
                                  }
                                  if (coupon.max_uses !== null && coupon.times_used >= coupon.max_uses) {
                                    toast.error("This coupon has reached its usage limit");
                                    setCouponLoading(false);
                                    return;
                                  }
                                } else {
                                  if (coupon.is_used) {
                                    toast.error("This coupon has already been used");
                                    setCouponLoading(false);
                                    return;
                                  }
                                  if (coupon.user_id !== user.id) {
                                    toast.error("This coupon is not valid for your account");
                                    setCouponLoading(false);
                                    return;
                                  }
                                }

                                // Compute discount value (percent vs fixed)
                                const basePrice = (selectedService?.price || 0) +
                                  selectedAddons.reduce((sum, a) => sum + a.price, 0);
                                const discountValue = coupon.discount_type === "percent"
                                  ? Math.round((basePrice * coupon.discount_amount) / 100 * 100) / 100
                                  : coupon.discount_amount;

                                setAppliedCoupons(prev => [...prev, {
                                  code: coupon.code,
                                  discount_amount: discountValue,
                                  id: coupon.id,
                                  is_promotional: coupon.is_promotional,
                                  discount_type: coupon.discount_type,
                                }]);
                                setCouponCode("");
                                const label = coupon.discount_type === "percent"
                                  ? `${coupon.discount_amount}% off ($${discountValue})`
                                  : `$${discountValue} off`;
                                toast.success(`Coupon applied! ${label}`);
                              } catch (err: any) {
                                toast.error("Failed to apply coupon");
                              }
                              setCouponLoading(false);
                            }}
                            className="text-xs tracking-widest uppercase"
                          >
                            {couponLoading ? <Loader2 size={14} className="animate-spin" /> : "Apply"}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Referral Code Input */}
                    {!adminMode && (
                      <div className="border-t border-border pt-4 mt-2">
                        <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 flex items-center gap-2">
                          <Gift size={14} /> Referral Code
                        </p>
                        {referralApplied ? (
                          <div className="flex items-center justify-between p-3 border border-green-500/30 bg-green-500/5">
                            <div className="flex items-center gap-2">
                              <Check size={14} className="text-green-600" />
                              <code className="font-mono font-bold text-xs tracking-widest">{referralCodeInput.toUpperCase()}</code>
                              <span className="text-sm text-green-600 font-semibold">Applied</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setReferralApplied(false); setReferralCodeInput(""); }}
                              className="text-xs text-muted-foreground"
                            >
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <Input
                              value={referralCodeInput}
                              onChange={(e) => setReferralCodeInput(e.target.value.toUpperCase())}
                              placeholder="Enter referral code"
                              className="bg-background border-border/50 font-mono tracking-widest uppercase max-w-[200px]"
                            />
                            <Button
                              variant="outline"
                              disabled={referralLoading || referralCodeInput.length < 3}
                              onClick={async () => {
                                setReferralLoading(true);
                                try {
                                  const { data: referrerProfile } = await supabase
                                    .from("profiles")
                                    .select("user_id, full_name")
                                    .eq("referral_code", referralCodeInput.toUpperCase())
                                    .maybeSingle();

                                  if (!referrerProfile || referrerProfile.user_id === user.id) {
                                    toast.error("Invalid referral code or you can't use your own code");
                                    setReferralLoading(false);
                                    return;
                                  }

                                  setReferralApplied(true);
                                  toast.success(`Referral from ${referrerProfile.full_name || "a friend"} applied!`);
                                } catch {
                                  toast.error("Failed to verify referral code");
                                }
                                setReferralLoading(false);
                              }}
                              className="text-xs tracking-widest uppercase"
                            >
                              {referralLoading ? <Loader2 size={14} className="animate-spin" /> : "Apply"}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Payment type selection - not for admin */}
                    {!adminMode && (
                      <div className="border-t border-border pt-4 mt-4">
                        <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-3 flex items-center gap-2">
                          <CreditCard size={14} /> Payment Option
                        </p>
                        {!effectiveDepositEnabled ? (
                          <div className="p-4 border border-primary bg-primary/10 text-center">
                            <p className="text-sm font-semibold">No Payment Required</p>
                            <p className="text-xs text-muted-foreground mt-1">Pay in person at your appointment</p>
                          </div>
                        ) : computedDeposit > 0 ? (
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              onClick={() => setPaymentType("deposit")}
                              className={`p-4 border text-center transition-all ${
                                paymentType === "deposit"
                                  ? "border-primary bg-primary/10"
                                  : "border-border bg-background hover:border-primary/50"
                              }`}
                            >
                              <p className="text-sm font-semibold">${computedDeposit} Deposit</p>
                              <p className="text-xs text-muted-foreground mt-1">Pay rest in person</p>
                            </button>
                            <button
                              onClick={() => {
                                setPaymentType("full");
                                if (appliedCoupons.length === 0 && availableCoupons.length > 0) {
                                  setAppliedCoupons(availableCoupons.map(c => ({ code: c.code, discount_amount: c.discount_amount, id: c.id })));
                                  const total = availableCoupons.reduce((sum, c) => sum + c.discount_amount, 0);
                                  toast.success(`${availableCoupons.length} coupon(s) auto-applied! $${total} off`);
                                }
                              }}
                              className={`p-4 border text-center transition-all ${
                                paymentType === "full"
                                  ? "border-primary bg-primary/10"
                                  : "border-border bg-background hover:border-primary/50"
                              }`}
                            >
                              <p className="text-sm font-semibold">${selectedService?.price} Full</p>
                              <p className="text-xs text-muted-foreground mt-1">Pay everything now</p>
                              {appliedCoupons.length === 0 && availableCoupons.length > 0 && paymentType !== "full" && (
                                <p className="text-[10px] text-primary mt-1 font-medium">Coupons auto-apply!</p>
                              )}
                            </button>
                          </div>
                        ) : (
                          <div className="p-4 border border-primary bg-primary/10 text-center">
                            <p className="text-sm font-semibold">${selectedService?.price} Full Payment</p>
                            <p className="text-xs text-muted-foreground mt-1">No deposit required — pay in person</p>
                          </div>
                        )}
                        {effectiveDepositEnabled && computedDeposit > 0 && (
                          <p className="text-xs text-muted-foreground mt-2 text-center">
                            You'll pay <span className="text-primary font-semibold">${paymentType === "deposit" ? computedDeposit : selectedService?.price}</span> now via Stripe
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Inspiration photo upload */}
                <div className="max-w-lg mx-auto mb-8">
                  <div className="bg-card border border-border/50 p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Camera size={16} className="text-primary" />
                      <h4 className="font-serif text-lg italic">Have photo inspiration?</h4>
                    </div>
                    <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                      Upload it here so Nijah can see exactly what you're envisioning. Optional, but very helpful! (JPG/PNG, up to 10MB each)
                    </p>

                    {inspirationPhotos.length > 0 && (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-4">
                        {inspirationPhotos.map((p) => (
                          <div key={p.id} className="relative aspect-square group">
                            <img
                              src={p.url}
                              alt="Inspiration"
                              className="w-full h-full object-cover border border-border/50"
                            />
                            <button
                              type="button"
                              onClick={() => removeInspirationPhoto(p)}
                              aria-label="Remove photo"
                              className="absolute top-1 right-1 p-1 rounded-full bg-background/90 text-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive hover:text-destructive-foreground"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <input
                      ref={inspirationInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => uploadInspirationPhotos(e.target.files)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => inspirationInputRef.current?.click()}
                      disabled={inspirationUploading}
                      className="w-full rounded-none text-xs tracking-[0.2em] uppercase border-primary/40 hover:bg-primary/10"
                    >
                      {inspirationUploading ? (
                        <><Loader2 size={14} className="animate-spin mr-2" /> Uploading…</>
                      ) : (
                        <><Plus size={14} className="mr-2" /> {inspirationPhotos.length > 0 ? "Add Another Photo" : "Upload Inspiration Photo"}</>
                      )}
                    </Button>
                  </div>
                </div>

                {!adminMode && isReturningClient && (
                  <div className="max-w-lg mx-auto mb-8" data-policies-section>
                    <div className={`p-5 border text-center transition-colors ${confirmAcknowledged ? "border-primary/50 bg-primary/5" : policyAttemptCount >= 1 ? "border-destructive" : "border-border/60 bg-card"}`}>
                      <p className="font-serif text-xl italic mb-2">One-tap policy acceptance</p>
                      <p className="text-sm font-extralight text-muted-foreground mb-4">
                        Accept the <Link to="/cancellation-policy" className="text-primary underline" target="_blank">booking policies</Link> here to finish booking this appointment. This includes the cancellation{computedDeposit > 0 ? <>, ${computedDeposit} non-refundable deposit</> : null} and late policy terms, plus consent to receive appointment SMS &amp; email updates (reply STOP to opt out).
                      </p>
                      <Button
                        onClick={acceptAllPolicies}
                        disabled={confirmAcknowledged}
                        className="rounded-none px-8 py-5 text-xs tracking-[0.3em] uppercase font-medium"
                      >
                        {confirmAcknowledged ? <><Check size={14} className="mr-2" /> Policies Accepted</> : "Accept Policies"}
                      </Button>
                    </div>
                  </div>
                )}

                {!adminMode && !isReturningClient && (
                  <>
                    <div className="max-w-lg mx-auto mb-8" data-policies-section>

                      <PoliciesSection
                        interactive
                        policies={policies}
                        confirmedPolicies={confirmedPolicies}
                        onConfirmPolicy={(index) => {
                          setConfirmedPolicies((prev) => ({ ...prev, [index]: true }));
                          setPolicyAttemptCount(0);
                          setHintPulse(false);
                        }}
                        hintPulse={hintPulse}
                        highlightUnconfirmed={policyAttemptCount >= 2}
                      />
                    </div>
                    {Object.keys(confirmedPolicies).length === policies.length && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="max-w-lg mx-auto mb-8"
                      >
                        <div className="flex items-start gap-3 p-4 border border-primary/30 bg-card">
                          <Checkbox
                            id="confirm-ack"
                            checked={confirmAcknowledged}
                            onCheckedChange={(checked) => setConfirmAcknowledged(checked === true)}
                            className="mt-0.5"
                          />
                          <label htmlFor="confirm-ack" className="text-sm font-light text-muted-foreground leading-relaxed cursor-pointer">
                            I confirm my booking and acknowledge the <Link to="/cancellation-policy" className="text-primary underline hover:text-primary/80" target="_blank">policies</Link> above{computedDeposit > 0 ? <>, including the ${computedDeposit} non-refundable deposit</> : null}, cancellation, and late policy terms. I consent to receive recurring <Link to="/sms-consent" className="text-primary underline hover:text-primary/80" target="_blank">SMS & email notifications</Link> from In Paradise Beauty about this appointment (up to ~5 messages per appointment). Msg & data rates may apply. Reply STOP to opt out, HELP for help.
                          </label>
                        </div>
                      </motion.div>
                    )}
                  </>
                )}

                <div className="max-w-lg mx-auto">
                  <Button
                    onClick={() => {
                      if (!adminMode && !confirmAcknowledged) {
                        const allPoliciesConfirmed = Object.keys(confirmedPolicies).length === policies.length;
                        if (!allPoliciesConfirmed) {
                          setPolicyAttemptCount((prev) => prev + 1);
                          setHintPulse(true);
                          setTimeout(() => setHintPulse(false), 2000);
                          // Scroll to policies
                          const policiesEl = document.querySelector('[data-policies-section]');
                          policiesEl?.scrollIntoView({ behavior: "smooth", block: "center" });
                          return;
                        }
                        // Policies confirmed but checkbox not checked
                        toast.error("Please check the confirmation box to proceed.");
                        return;
                      }
                      handleConfirm();
                    }}
                    disabled={submitting || processingPayment}
                    className="w-full rounded-none py-5 text-xs font-medium tracking-[0.3em] uppercase border border-primary hover:bg-primary/90"
                  >
                    {processingPayment ? (
                      <><Loader2 size={14} className="animate-spin mr-2" /> Redirecting to Payment…</>
                    ) : submitting ? (
                      "Booking..."
                    ) : adminMode ? (
                      "Confirm Booking"
                    ) : !effectiveDepositEnabled ? (
                      "Confirm Booking"
                    ) : (
                      (() => {
                        const discount = appliedCoupons.reduce((sum, c) => sum + c.discount_amount, 0);
                        const base = paymentType === "deposit" ? computedDeposit : (selectedService?.price || 0);
                        const final_amount = Math.max(0, base - (paymentType === "full" ? discount : 0));
                        return `Pay $${final_amount} & Confirm`;
                      })()
                    )}
                  </Button>
                </div>

                <div className="flex justify-start mt-8">
                  <Button variant="outline" onClick={() => setStep(1)} className="rounded-none px-8 border-primary/30 hover:bg-primary/10 tracking-[0.2em] uppercase text-xs font-medium">
                    <ArrowLeft size={14} /> Back
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
            )}
          </AnimatePresence>
        </div>
      </section>
      <Footer />

      {/* Express policy upgrade announcement (shown once per client) */}
      {expressPolicyOpen && <Confetti />}
      <Dialog open={expressPolicyOpen} onOpenChange={setExpressPolicyOpen}>
        <DialogContent className="sm:max-w-sm text-center">
          <DialogHeader>
            <DialogTitle className="font-serif italic text-2xl">Congratulations!</DialogTitle>
            <DialogDescription className="font-extralight">
              Your account has been upgraded to bypass additional policy acceptance. You will only have to accept
              policies once per appointment.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center gap-2">
            <Button onClick={acceptAllPolicies} className="rounded-none text-xs tracking-[0.2em] uppercase">
              Accept &amp; Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={lashInspoOpen} onOpenChange={setLashInspoOpen}>

        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif italic flex items-center gap-2">
              <Sparkles size={18} className="text-primary" />
              Share your lash inspiration
            </DialogTitle>
            <DialogDescription>
              Upload a photo of the lash set you'd love. This helps us nail your perfect look — but it's completely optional.
            </DialogDescription>
          </DialogHeader>

          {inspirationPhotos.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {inspirationPhotos.map((p) => (
                <div key={p.id} className="relative aspect-square overflow-hidden border border-border">
                  <img src={p.url} alt="Inspiration" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeInspirationPhoto(p)}
                    className="absolute top-1 right-1 bg-background/80 rounded-full p-0.5 hover:bg-background"
                    aria-label="Remove inspiration photo"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            ref={inspirationInputRef}
            onChange={(e) => uploadInspirationPhotos(e.target.files)}
          />

          <Button
            type="button"
            variant="outline"
            onClick={() => inspirationInputRef.current?.click()}
            disabled={inspirationUploading}
            className="w-full rounded-none tracking-[0.2em] uppercase text-xs"
          >
            {inspirationUploading ? (
              <><Loader2 size={14} className="mr-2 animate-spin" /> Uploading…</>
            ) : (
              <><Plus size={14} className="mr-2" /> {inspirationPhotos.length > 0 ? "Add another photo" : "Upload a photo"}</>
            )}
          </Button>

          <DialogFooter className="sm:justify-between gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setLashInspoOpen(false);
                setStep(1);
              }}
              className="text-xs tracking-[0.2em] uppercase"
            >
              Skip for now
            </Button>
            <Button
              type="button"
              onClick={() => {
                setLashInspoOpen(false);
                setStep(1);
              }}
              disabled={inspirationUploading}
              className="rounded-none text-xs tracking-[0.2em] uppercase"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Book;
